import { Fragment, useMemo, useState, useCallback } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { DataGrid, KeenIcon } from '@/components';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  useGetArticleVersionsQuery,
  usePublishArticleVersionMutation,
} from '@/services/ArticleService';
import { ArticleVersion } from '@/types/cms.types';
import { toAbsoluteUrl } from '@/utils';
import { PublishArticleModal } from './PublishArticleModal';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate } from '@/utils/Date';

const ArticleVersions = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const { data: versionsData, isLoading } = useGetArticleVersionsQuery(
    id as string,
    {
      skip: !id,
    }
  );

  const [publishVersion] = usePublishArticleVersionMutation();
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);

  const handlePublishVersion = useCallback((version: number) => {
    setSelectedVersion(version);
    setPublishModalOpen(true);
  }, []);

  const handleConfirmPublish = useCallback(async () => {
    if (selectedVersion === null) return;

    try {
      await publishVersion({
        id: id as string,
        version: selectedVersion,
      }).unwrap();
      enqueueSnackbar(`Version ${selectedVersion} published successfully`, {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('Failed to publish version', { variant: 'error' });
    } finally {
      setPublishModalOpen(false);
      setSelectedVersion(null);
    }
  }, [selectedVersion, publishVersion, id, enqueueSnackbar]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'coverImageUrl',
        header: 'Image',
        cell: ({ row }: { row: { original: ArticleVersion } }) => (
          <img
            src={
              row.original.coverImageUrl
                ? toAbsoluteUrl(row.original.coverImageUrl)
                : '/media/avatars/harbor-default.jpg'
            }
            className="size-10 rounded-lg object-cover"
            alt={row.original.title || 'Article'}
          />
        ),
        meta: { className: 'w-20' },
      },
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }: { row: { original: ArticleVersion } }) => (
          <span className="font-medium text-gray-800">
            {row.original.title}
          </span>
        ),
      },
      {
        accessorKey: 'category',
        header: 'Category',
        cell: ({ row }: { row: { original: ArticleVersion } }) => (
          <span className="badge badge-sm badge-outline text-nowrap">
            {row.original.category}
          </span>
        ),
      },
      {
        accessorKey: 'version',
        header: 'Version',
        cell: ({ row }: { row: { original: ArticleVersion } }) => (
          <span className="badge badge-sm badge-primary">
            v{row.original.version}
          </span>
        ),
        meta: { className: 'w-24' },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        cell: ({ row }: { row: { original: ArticleVersion } }) => (
          <span className="text-gray-600">
            {row.original.createdAt
              ? formatDisplayDate(row.original.createdAt)
              : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'publishedBy',
        header: 'Published By',
        cell: ({ row }: { row: { original: ArticleVersion } }) => {
          if (!row.original.publishedBy) {
            return <span className="text-gray-400">-</span>;
          }
          return (
            <div className="flex flex-col">
              <span className="text-gray-800 font-medium">
                {row.original.publishedBy.name}
              </span>
              <span className="text-gray-500 text-sm contrast:text-white/50">
                {row.original.publishedBy.email}
              </span>
            </div>
          );
        },
      },
      {
        id: 'publish',
        header: 'Publish',
        cell: ({
          row,
        }: {
          row: { original: ArticleVersion; index: number };
        }) => {
          // Hide publish button for the first article (index 0)
          if (row.index === 0) {
            return null;
          }
          return (
            <button
              className="btn btn-sm btn-success"
              onClick={() => handlePublishVersion(row.original.version)}
              disabled={!hasPermission('article_management_publish')}
            >
              <KeenIcon icon="check-circle" className="mr-1" />
              Publish
            </button>
          );
        },
        meta: { className: 'w-32' },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }: { row: { original: ArticleVersion } }) => (
          <div className="flex gap-2">
            <button
              className="btn btn-sm btn-icon btn-light"
              onClick={() =>
                navigate(
                  `/article-management/view/${id}?version=${row.original.version}`
                )
              }
              title="View"
              aria-label="View"
            >
              <KeenIcon icon="eye" />
            </button>
          </div>
        ),
        meta: { className: 'w-24' },
      },
    ],
    [navigate, id, handlePublishVersion, hasPermission]
  );

  if (isLoading) {
    return <div className="p-10 text-center">Loading versions...</div>;
  }

  return (
    <Fragment>
      <section aria-labelledby="article-versions-heading">
        {currentLayout?.name === 'demo1-layout' && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle
                  id="article-versions-heading"
                  text="Article Versions"
                />
                <ToolbarDescription>
                  View and manage article version history
                </ToolbarDescription>
              </ToolbarHeading>
              <ToolbarActions>
                <button
                  className="btn btn-sm btn-light h-10"
                  onClick={() => navigate(`/article-management`)}
                >
                  <KeenIcon icon="arrow-left" className="mr-1" />
                  Back to Articles
                </button>
              </ToolbarActions>
            </Toolbar>
          </Container>
        )}

        <div className="container-fixed">
          <div className="card">
            <div className="card-body">
              <DataGrid
                columns={columns}
                data={versionsData?.data || []}
                rowSelect={false}
                loading={isLoading}
                pagination={{
                  size: 10,
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <PublishArticleModal
        open={publishModalOpen}
        onClose={() => {
          setPublishModalOpen(false);
          setSelectedVersion(null);
        }}
        onConfirm={handleConfirmPublish}
      />
    </Fragment>
  );
};

export { ArticleVersions };
